import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Home as HomeIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchMachine } from '../../store/slices/machinesSlice';
import axios from 'axios';

interface QRCodeResponse {
  qr_code: string;
  machine_id: string;
  format: string;
  encoding: string;
}

export const MachineQRCodePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentMachine } = useAppSelector((state) => state.machines);

  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      // Fetch machine details if not already loaded
      if (!currentMachine || currentMachine.id !== id) {
        dispatch(fetchMachine(id));
      }

      // Fetch QR code
      fetchQRCode(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  const fetchQRCode = async (machineId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';

      const response = await axios.get<QRCodeResponse>(
        `${apiUrl}/machines/${machineId}/qr`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setQrCode(response.data.qr_code);
    } catch (err: any) {
      console.error('Failed to fetch QR code:', err);
      setError(err.response?.data?.message || 'QR-Code konnte nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/machines/${id}`);
  };

  const handleDownload = () => {
    if (!qrCode || !currentMachine) return;

    // Create a download link
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrCode}`;
    link.download = `machine-qr-${currentMachine.name.replace(/\s+/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (!qrCode || !currentMachine) return;

    try {
      // Convert base64 to blob
      const byteCharacters = atob(qrCode);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      const file = new File([blob], `machine-qr-${currentMachine.name}.png`, { type: 'image/png' });

      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `QR-Code für ${currentMachine.name}`,
          text: `QR-Code zum Scannen der Maschine "${currentMachine.name}"`,
          files: [file],
        });
      } else {
        // Fallback: copy link to clipboard
        const machineUrl = `${window.location.origin}/machines/${id}`;
        await navigator.clipboard.writeText(machineUrl);
        alert('Link wurde in die Zwischenablage kopiert');
      }
    } catch (err) {
      console.error('Share failed:', err);
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack}>
          Zurück zur Maschine
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            QR-Code wird generiert...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Breadcrumbs - Hidden in print */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, '@media print': { display: 'none' } }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/machines')}
        >
          Maschinen
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={handleBack}
        >
          {currentMachine?.name || 'Maschine'}
        </Link>
        <Typography color="text.primary">QR-Code</Typography>
      </Breadcrumbs>

      {/* Header - Hidden in print */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{ '@media print': { display: 'none' } }}
      >
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            QR-Code
          </Typography>
        </Box>

        <Box display="flex" gap={1}>
          {'share' in navigator && (
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={handleShare}
              sx={{ display: { xs: 'flex', sm: 'none' } }}
            >
              Teilen
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Drucken
          </Button>
        </Box>
      </Box>

      {/* QR Code Display */}
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            '@media print': {
              boxShadow: 'none',
              border: '1px solid #ccc',
            }
          }}
        >
          {/* Machine Info */}
          {currentMachine && (
            <>
              <Typography variant="h5" gutterBottom>
                {currentMachine.name}
              </Typography>
              {currentMachine.location && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  Standort: {currentMachine.location}
                </Typography>
              )}
              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* QR Code Image */}
          {qrCode && (
            <Box sx={{ mb: 3 }}>
              <img
                src={`data:image/png;base64,${qrCode}`}
                alt="Machine QR Code"
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: 'auto',
                }}
              />
            </Box>
          )}

          {/* Instructions */}
          <Card variant="outlined" sx={{ mt: 3, '@media print': { display: 'none' } }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Verwendung
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scannen Sie diesen QR-Code mit der Kamera Ihres Mobilgeräts oder der
                Maschinenmatrix-App, um direkt zu den Details dieser Maschine zu gelangen.
              </Typography>
            </CardContent>
          </Card>

          {/* Print-only footer */}
          <Box sx={{ display: 'none', '@media print': { display: 'block', mt: 4, pt: 2, borderTop: '1px solid #ccc' } }}>
            <Typography variant="body2" color="text.secondary">
              Maschinenmatrix | QR-Code generiert am {new Date().toLocaleDateString('de-DE')}
            </Typography>
            {id && (
              <Typography variant="caption" color="text.secondary">
                Maschinen-ID: {id}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};